import type { ModalSessionOffer } from '@voll-intelligence/domain';

import { ChatUiMoneySchema } from '../../shared/schema/money_schema.js';
import { HotelAmenitySchema, type ChatUiHotelAmenity } from '../shared/hotel_amenity_schema.js';
import type {
  HotelOfferSource,
  HotelPolicyLimits,
  HotelRoomSource,
} from './hotel_offer_projection.js';

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function records(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? value.map(record).filter((item) => Object.keys(item).length) : [];
}

function str(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/** `str` cru só barra a string VAZIA — um `description`/`editorialSummary`
 *  só-espaços do fornecedor passaria como linha em branco no retrato (mesmo
 *  padrão do helper `text()` de `hotel_offer_projection.ts`). Cirúrgico nos
 *  campos de texto de terceiro que viram LINHA na tela/no retrato
 *  (`description`, `editorialSummary`, `policyName`): os demais campos deste
 *  arquivo continuam em `str`, sem trim. */
function trimmedText(value: unknown): string | undefined {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  return trimmed.length > 0 ? trimmed : undefined;
}

function num(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function bool(value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined;
}

function moneyAmount(value: unknown): number | undefined {
  return num(record(value).amount);
}

function moneyCurrency(value: unknown): string | undefined {
  return str(record(value).currency);
}

function photosFromData(data: Record<string, unknown>): HotelOfferSource['photos'] | undefined {
  const photos = records(data.photos)
    .map((photo) => ({
      ...(str(photo.url) ? { url: str(photo.url) } : {}),
      ...(str(photo.description) ? { description: str(photo.description) } : {}),
    }))
    .filter((photo): photo is { url: string; description?: string } => photo.url !== undefined);
  return photos.length > 0 ? photos : undefined;
}

/** O modal-session normaliza a comodidade na raiz (`{ code?, label }`). A string
 *  crua ainda chega de sessão gravada antes dessa normalização — vira `{ label }`,
 *  exatamente o que o card já exibia. */
function amenitiesFromData(data: Record<string, unknown>): ChatUiHotelAmenity[] | undefined {
  const amenities = (Array.isArray(data.amenities) ? data.amenities : [])
    .map((value) => HotelAmenitySchema.safeParse(value))
    .filter((parsed) => parsed.success)
    .map((parsed) => parsed.data);
  return amenities.length > 0 ? amenities : undefined;
}

function policyStatus(value: unknown): HotelOfferSource['policyStatus'] | undefined {
  return value === 'within_policy' || value === 'out_of_policy' || value === 'unknown'
    ? value
    : undefined;
}

/** As regras do `HotelPolicies` do fornecedor, na ordem em que o motivo é
 *  contado ao viajante: janela de antecedência, "mais barato", tetos de preço
 *  (do mais geral ao mais específico) e as exigências de tarifa. É a ordem
 *  ESTÁVEL de `policyViolations` — quem renderiza não reordena. */
export const HOTEL_POLICY_RULE_KEYS = [
  'bookingWindow',
  'bookingWindowInternational',
  'cheapestPrice',
  'cheapestHotel',
  'maxPrice',
  'maxPriceInternational',
  'maxPricePerCity',
  'maxPricePerCountry',
  'onlyRateWithBreakfast',
  'onlyRateRefundable',
] as const;

/** `modalData.policyRules` (`Record<string, boolean | null>`, `false` = regra
 *  QUEBRADA) → as chaves quebradas na ordem canônica. `true` é regra cumprida e
 *  `null` é "sem veredito": nenhum dos dois é motivo. Chave desconhecida some
 *  em silêncio — o warn do produtor é no modal-session, que é onde dá para
 *  consertar.
 *
 *  #4284: o `onlyRateRefundable` por tarifa do hotels-api acompanha o `within`,
 *  não o reembolso — chega `false` em tarifa `refundable: true`. Quando a
 *  própria tarifa (`rate`) se diz reembolsável, esse `false` não é motivo. */
export function policyViolationsFromRules(
  value: unknown,
  rate: Record<string, unknown> = {},
): string[] | undefined {
  const rules = record(value);
  const broken = HOTEL_POLICY_RULE_KEYS.filter(
    (key) => rules[key] === false && !(key === 'onlyRateRefundable' && rate.refundable === true),
  );
  return broken.length > 0 ? [...broken] : undefined;
}

/** Cidade comparável: sem acento e sem caixa — a política escreve "sao paulo" e
 *  a oferta "São Paulo", e a mesma cidade não pode virar duas. */
function citySlug(value: unknown): string | undefined {
  const text = typeof value === 'string' ? value.trim() : '';
  if (!text) return undefined;
  return text
    .normalize('NFD')
    .replaceAll(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

/** O teto por CIDADE que vale para esta oferta: só a entrada cuja cidade casa
 *  com a da oferta. Sem cidade na oferta não há como escolher uma — e um teto
 *  de outra cidade seria uma mentira precisa. */
function maxNightlyCityFor(entries: unknown, city: string | undefined): number | undefined {
  const wanted = citySlug(city);
  if (!wanted) return undefined;
  const match = records(entries).find((entry) => citySlug(entry.city) === wanted);
  return match ? priceCap(match.price) : undefined;
}

/** O teto por PAÍS que vale para esta oferta: só a entrada cujo `code` casa
 *  com o país da oferta (sem caixa e sem espaços). Sem país na oferta não há
 *  como escolher uma — e um teto de outro país seria uma mentira precisa. */
function maxNightlyCountryFor(entries: unknown, country: string | undefined): number | undefined {
  const wanted = country?.trim().toUpperCase();
  if (!wanted) return undefined;
  const match = records(entries).find(
    (entry) => typeof entry.code === 'string' && entry.code.trim().toUpperCase() === wanted,
  );
  return match ? priceCap(match.price) : undefined;
}

/** Só quando a regra está LIGADA: `false` e ausente dizem a mesma coisa (a
 *  regra não vale) e não merecem uma linha no cabeçalho de limites. */
function onlyTrue(value: unknown): true | undefined {
  return value === true ? true : undefined;
}

/** O mesmo para os tetos de preço: `0` é teto desligado (a tarifa de 380 não
 *  quebra `maxPrice: 0`), e "acima de 0" seria um limite que não existe. */
function priceCap(value: unknown): number | undefined {
  const cap = num(value);
  return cap !== undefined && cap > 0 ? cap : undefined;
}

/** E para a antecedência (#5903): `0` dias é sem regra, nunca "antecedência
 *  mínima de 0 dias" no cabeçalho do agente. */
const positiveDays = priceCap;

/** `modalData.policyDetails` (cópia do `HotelTravelerPolicyFilter`) → os
 *  limites como o card e o agente os leem. Preços são números SEM moeda: a
 *  origem não a informa. */
export function policyLimitsFromDetails(
  value: unknown,
  city: string | undefined,
  country: string | undefined,
): HotelPolicyLimits | undefined {
  const details = record(value);
  if (Object.keys(details).length === 0) return undefined;
  const limits: HotelPolicyLimits = {
    // `trimmedText`: um `policyName` só-espaços viraria uma linha em branco no
    // cabeçalho de limites (mesma razão dos textos de terceiro acima).
    ...(trimmedText(details.policyName) ? { policyName: trimmedText(details.policyName) } : {}),
    ...(priceCap(details.maxPrice) === undefined ? {} : { maxNightly: priceCap(details.maxPrice) }),
    ...(priceCap(details.maxPriceInternational) === undefined
      ? {}
      : { maxNightlyInternational: priceCap(details.maxPriceInternational) }),
    ...(maxNightlyCityFor(details.maxPricePerCity, city) === undefined
      ? {}
      : { maxNightlyCity: maxNightlyCityFor(details.maxPricePerCity, city) }),
    ...(maxNightlyCountryFor(details.maxPricePerCountry, country) === undefined
      ? {}
      : { maxNightlyCountry: maxNightlyCountryFor(details.maxPricePerCountry, country) }),
    ...(num(details.minRating) === undefined ? {} : { minRating: num(details.minRating) }),
    ...(onlyTrue(details.onlyRateWithBreakfast) ? { onlyBreakfast: true } : {}),
    ...(onlyTrue(details.onlyRateRefundable) ? { onlyRefundable: true } : {}),
    ...(positiveDays(details.bookingWindow) === undefined
      ? {}
      : { bookingWindowDays: positiveDays(details.bookingWindow) }),
    ...(positiveDays(details.bookingWindowInternational) === undefined
      ? {}
      : { bookingWindowInternationalDays: positiveDays(details.bookingWindowInternational) }),
    ...(onlyTrue(details.cheapestPrice) ? { cheapestRateOnly: true } : {}),
    ...(onlyTrue(details.cheapestHotel) ? { cheapestHotelOnly: true } : {}),
  };
  return Object.keys(limits).length > 0 ? limits : undefined;
}

function roomBaseFromData(data: Record<string, unknown>): HotelRoomSource {
  const supplier = record(data.supplier);
  const cancellationPolicies = records(data.cancellationPolicies);
  return {
    ...(str(data.description) ? { description: str(data.description) } : {}),
    ...(str(data.boardDescription) ? { boardDescription: str(data.boardDescription) } : {}),
    ...(bool(data.refundable) === undefined ? {} : { refundable: bool(data.refundable) }),
    ...(bool(data.breakfast) === undefined ? {} : { breakfast: bool(data.breakfast) }),
    ...(moneyAmount(data.totalSellingPrice) === undefined
      ? {}
      : { totalAmount: moneyAmount(data.totalSellingPrice) }),
    ...(moneyCurrency(data.totalSellingPrice)
      ? { currency: moneyCurrency(data.totalSellingPrice) }
      : {}),
    ...(str(supplier.name) ? { supplierName: str(supplier.name) } : {}),
    ...(str(supplier.image) ? { supplierImage: str(supplier.image) } : {}),
    ...(str(supplier.icon) ? { supplierIcon: str(supplier.icon) } : {}),
    ...(cancellationPolicies.length > 0 ? { cancellationPolicies } : {}),
  };
}

function roomEnrichmentFromData(data: Record<string, unknown>): HotelRoomSource {
  const photos = photosFromData(data);
  const amenities = amenitiesFromData(data);
  const policyViolations = policyViolationsFromRules(data.policyRules, data);
  return {
    ...(str(data.name) ? { name: str(data.name) } : {}),
    ...(str(data.about) ? { about: str(data.about) } : {}),
    ...(photos ? { photos } : {}),
    ...(amenities ? { amenities } : {}),
    ...(policyStatus(data.policyStatus) ? { policyStatus: policyStatus(data.policyStatus) } : {}),
    ...(policyViolations ? { policyViolations } : {}),
    ...(num(data.quantity) === undefined ? {} : { quantity: num(data.quantity) }),
    ...(moneyAmount(data.totalPerNight) === undefined
      ? {}
      : { totalPerNightAmount: moneyAmount(data.totalPerNight) }),
  };
}

/** O fornecedor das tarifas de acordo corporativo. */
const AGREEMENT_SUPPLIER = 'acordo';

/** Tarifa negociada é a que vem do fornecedor "Acordo" (#6914) — e só ela.
 *  Nome do quarto, valor e regime não entram: o sistema de reservas não
 *  identifica o acordo por eles, e o casamento por nome + total com o
 *  `cheapestRoomRateAgreement` errava (#5922). A marcação `localTariff` dos
 *  outros fornecedores também não conta. Sem diferenciar maiúsculas: uma
 *  variação de grafia não pode apagar o selo em silêncio. */
function agreementSupplier(data: Record<string, unknown>): boolean {
  return str(record(data.supplier).name)?.trim().toLowerCase() === AGREEMENT_SUPPLIER;
}

function roomFromData(data: Record<string, unknown>): HotelRoomSource {
  return {
    ...roomBaseFromData(data),
    ...roomEnrichmentFromData(data),
    ...(agreementSupplier(data) ? { negotiated: true } : {}),
  };
}

function roomsFromModalData(data: Record<string, unknown>): HotelRoomSource[] | undefined {
  const hydratedRooms = records(data.rooms).map(roomFromData);
  if (hydratedRooms.length > 0) return hydratedRooms;
  const cheapest = record(data.cheapestRoom);
  return Object.keys(cheapest).length > 0 ? [roomFromData(cheapest)] : undefined;
}

function noiseTokensFromModalData(data: Record<string, unknown>): string[] | undefined {
  if (!Array.isArray(data.roomNoiseTokens)) return undefined;
  const tokens = data.roomNoiseTokens.filter((token): token is string => typeof token === 'string');
  return tokens.length > 0 ? tokens : undefined;
}

const MAX_REVIEW_SNIPPETS = 3;
const REVIEW_SNIPPET_MAX_CHARS = 200;

function reviewSnippetsFromModalData(data: Record<string, unknown>): string[] | undefined {
  if (!Array.isArray(data.reviews)) return undefined;
  const snippets = data.reviews
    .map((review) =>
      review && typeof review === 'object' ? (review as { text?: unknown }).text : undefined,
    )
    .filter((text): text is string => typeof text === 'string' && text.trim().length > 0)
    .map((text) => text.trim())
    .map((text) =>
      text.length > REVIEW_SNIPPET_MAX_CHARS ? `${text.slice(0, REVIEW_SNIPPET_MAX_CHARS)}…` : text,
    )
    .slice(0, MAX_REVIEW_SNIPPETS);
  return snippets.length > 0 ? snippets : undefined;
}

export function modalSessionHotelOffer(offer: ModalSessionOffer): HotelOfferSource {
  const data = record(offer.modalData);
  const rooms = roomsFromModalData(data);
  const noiseTokens = noiseTokensFromModalData(data);
  return {
    ...modalSessionOfferBase(offer),
    ...modalSessionOfferLocation(data),
    ...modalSessionOfferPreferences(offer, data),
    ...modalSessionOfferEnrichment(data),
    ...(rooms ? { rooms } : {}),
    ...(noiseTokens ? { noiseTokens } : {}),
  };
}

/** Sem `summary`: no wire do modal-session ele é "endereço - cidade"
 *  (`hotel_search_mapper.ts`), e `address`/`city` já chegam por `modalData`.
 *  Projetado, virava a frase do agente (✦) da Página do Hotel repetindo a
 *  linha de endereço logo acima (#3217 C9). O campo `summary` da oferta fica
 *  reservado a uma frase de verdade, que hoje ninguém produz. */
function modalSessionOfferBase(offer: ModalSessionOffer): HotelOfferSource {
  return {
    offerId: offer.offerId,
    title: offer.title ?? 'Hotel',
  };
}

function modalSessionOfferLocation(data: Record<string, unknown>): Partial<HotelOfferSource> {
  return {
    ...(str(data.city) ? { city: str(data.city) } : {}),
    ...(str(data.country) ? { country: str(data.country) } : {}),
    ...(str(data.address) ? { address: str(data.address) } : {}),
    ...(num(data.category) === undefined ? {} : { category: num(data.category) }),
    ...(num(data.rating) === undefined ? {} : { rating: num(data.rating) }),
  };
}

/** `modalData.refundableRateFrom` (#3404): dinheiro completo ou nada — metade
 *  (valor sem moeda) não se formata. */
function refundableRateFromData(
  data: Record<string, unknown>,
): Pick<HotelOfferSource, 'refundableRateFrom'> {
  const money = ChatUiMoneySchema.safeParse(data.refundableRateFrom);
  return money.success ? { refundableRateFrom: money.data } : {};
}

/** A política de cada agregado de tarifa (#5820): só o boolean afirmado viaja. */
function rateWithinPolicyFromData(
  data: Record<string, unknown>,
): Pick<
  HotelOfferSource,
  'breakfastRateWithinPolicy' | 'refundableRateWithinPolicy' | 'breakfastRefundableRateWithinPolicy'
> {
  const breakfast = bool(data.breakfastRateWithinPolicy);
  const refundable = bool(data.refundableRateWithinPolicy);
  const both = bool(data.breakfastRefundableRateWithinPolicy);
  return {
    ...(breakfast === undefined ? {} : { breakfastRateWithinPolicy: breakfast }),
    ...(refundable === undefined ? {} : { refundableRateWithinPolicy: refundable }),
    ...(both === undefined ? {} : { breakfastRefundableRateWithinPolicy: both }),
  };
}

function modalSessionOfferPreferences(
  offer: ModalSessionOffer,
  data: Record<string, unknown>,
): Partial<HotelOfferSource> {
  const policyViolations = policyViolationsFromRules(data.policyRules, data);
  const policyLimits = policyLimitsFromDetails(
    data.policyDetails,
    str(data.city),
    str(data.country),
  );
  // `data.selectedHotel` (o modal-session marca o hotel pesquisado em
  // `hotel_search_mapper.ts`) NÃO é projetado: `HotelOfferSchema` é `.strict()`
  // e o bundle do web-chat já carregado no navegador descartaria a oferta —
  // ver o comentário longo em `hotelOfferToChatUi`, o produtor final. As duas
  // linhas voltam juntas quando os bundles pré-2026-08-10 decaírem.
  return {
    ...(offer.total ? { priceAmount: offer.total.amount, currency: offer.total.currency } : {}),
    ...(bool(data.recommended) === undefined ? {} : { recommended: bool(data.recommended) }),
    ...(bool(data.agreedRate) === undefined ? {} : { agreedRate: bool(data.agreedRate) }),
    ...(bool(data.refundable) === undefined ? {} : { refundable: bool(data.refundable) }),
    ...(bool(data.breakfast) === undefined ? {} : { breakfast: bool(data.breakfast) }),
    ...(bool(data.hasBreakfastRate) === undefined
      ? {}
      : { hasBreakfastRate: bool(data.hasBreakfastRate) }),
    ...(bool(data.hasRefundableRate) === undefined
      ? {}
      : { hasRefundableRate: bool(data.hasRefundableRate) }),
    ...(bool(data.hasBreakfastRefundableRate) === undefined
      ? {}
      : { hasBreakfastRefundableRate: bool(data.hasBreakfastRefundableRate) }),
    ...rateWithinPolicyFromData(data),
    ...refundableRateFromData(data),
    ...(policyStatus(data.policyStatus) ? { policyStatus: policyStatus(data.policyStatus) } : {}),
    ...(policyViolations ? { policyViolations } : {}),
    ...(policyLimits ? { policyLimits } : {}),
    ...(str(data.thumbnailUrl) ? { thumbnailUrl: str(data.thumbnailUrl) } : {}),
  };
}

function modalSessionOfferEnrichment(data: Record<string, unknown>): Partial<HotelOfferSource> {
  const photos = photosFromData(data);
  const amenities = amenitiesFromData(data);
  const reviewSnippets = reviewSnippetsFromModalData(data);
  return {
    ...(num(data.latitude) === undefined ? {} : { latitude: num(data.latitude) }),
    ...(num(data.longitude) === undefined ? {} : { longitude: num(data.longitude) }),
    // `modalData.distanceKm` sai de `localization.distance` no
    // `hotel_search_mapper` — o modal-session já gravava e ninguém lia.
    ...(num(data.distanceKm) === undefined ? {} : { distanceKm: num(data.distanceKm) }),
    ...(photos ? { photos } : {}),
    ...(amenities ? { amenities } : {}),
    ...(num(data.reviewCount) === undefined ? {} : { reviewCount: num(data.reviewCount) }),
    ...(trimmedText(data.description) ? { description: trimmedText(data.description) } : {}),
    ...(trimmedText(data.editorialSummary)
      ? { editorialSummary: trimmedText(data.editorialSummary) }
      : {}),
    ...(reviewSnippets ? { reviewSnippets } : {}),
  };
}
